import { supabase } from "~/integrations/supabase/client";
import { CHILEAN_COMUNAS } from "~/constants/places";
import { calcLineTotal } from "~/utils/lineItemCalculations";
import { SalesRequest, SalesRequestItem } from "~/types/client";

interface ProductDetails {
  name: string;
  image?: string;
  internalId?: string;
}

/**
 * Generate a payment document HTML and open it in a new window for printing
 * This is used for prepaid/anticipado orders to provide payment instructions
 */
export async function generatePaymentDocumentHtml(
  selectedOrder: SalesRequest,
  clientName: string,
  seller: { name: string; lastname: string },
  items: SalesRequestItem[]
): Promise<boolean | "download"> {
  try {
    // Fetch product details for all products
    const productDetails: Record<string, ProductDetails> = {};

    if (items && items.length > 0) {
      // Get unique product IDs
      const productIds = [...new Set(items.map((item) => item.productId))];

      // Fetch product details
      const { data, error } = await supabase
        .from("products")
        .select("id, short_name, long_name, product_image_url, internal_id")
        .in("id", productIds);

      if (error) {
        console.error("Error fetching product details:", error);
        throw error;
      }

      // Create a map of product details
      data?.forEach((product) => {
        productDetails[product.id] = {
          name:
            product.long_name ||
            product.short_name ||
            `Producto ${product.id.substring(0, 8)}`,
          image: product.product_image_url,
          internalId: product.internal_id,
        };
      });
    }

    // Fetch client details to get tax_id (RUT) and activity
    const { data: clientData, error: clientError } = await supabase
      .from("clients")
      .select("*")
      .eq("id", selectedOrder.clientId)
      .single();

    if (clientError) {
      console.error("Error fetching client details:", clientError);
    }

    // Get client address details
    const { data: addressData, error: addressError } = await supabase
      .from("addresses")
      .select("street, number, comuna, city")
      .eq("client_id", selectedOrder.clientId)
      .eq("is_default", true)
      .single();

    if (addressError) {
      console.error("Error fetching address details:", addressError);
    }

    const clientTaxId = clientData?.tax_id || "XX.XXX.XXX-X";
    const clientPhone = clientData?.phone_number || "9 XXXXXXXX";
    const clientAddress = addressData
      ? `${addressData.street} ${addressData.number}`
      : "Av. Providencia";
    const clientComuna = addressData?.comuna
      ? CHILEAN_COMUNAS.find((c) => c.value === addressData.comuna)?.label ||
        addressData.comuna
      : "Vitacura";
    const clientCity = addressData?.city || "Santiago";

    // Calculate totals
    let subtotal = 0;
    items.forEach((item) => {
      subtotal += calcLineTotal(
        item.unitPrice,
        item.quantity,
        item.discountPercent ?? 0
      );
    });
    const iva = Math.round(subtotal * 0.19);
    const total = subtotal + iva;

    // Format the date
    const today = new Date();
    const formattedDate = `${today.getDate().toString().padStart(2, "0")}-${(today.getMonth() + 1).toString().padStart(2, "0")}-${today.getFullYear()}`;

    // Order number with formatting
    const orderNumber = selectedOrder.id.substring(0, 8);

    // Generate items HTML
    const itemsHtml = items
      .map(
        (item) => `
      <tr>
        <td>
          <div style="display: flex; align-items: center;">
            ${
              productDetails[item.productId]?.image
                ? `<img src="${productDetails[item.productId].image}" style="width: 40px; height: 40px; object-fit: cover; margin-right: 8px; border-radius: 4px;" alt="product" />`
                : ""
            }
            <div>
              <div style="font-weight: bold;">${
                productDetails[item.productId]?.name ||
                `Producto (${item.productId})`
              }</div>
              <div>COD: ${productDetails[item.productId]?.internalId || "-"}</div>
            </div>
          </div>
        </td>
        <td>${item.quantity}</td>
        <td>$${item.unitPrice.toLocaleString("es-CL")}</td>
        <td>${item.discountPercent ? `${item.discountPercent}%` : "-"}</td>
        <td>$${calcLineTotal(
          item.unitPrice,
          item.quantity,
          item.discountPercent ?? 0
        ).toLocaleString("es-CL")}</td>
      </tr>
    `
      )
      .join("");

    // Client activity (giro)
    const clientActivity = (clientData as any)?.activity;
    const activityHtml = clientActivity
      ? `<div><span class="info-label">GIRO:</span> ${clientActivity}</div>`
      : "";

    // Delivery address
    const deliveryAddressText = `${selectedOrder.deliveryStreet || ""} ${selectedOrder.deliveryNumber || ""} ${selectedOrder.deliveryComplement ? `, ${selectedOrder.deliveryComplement}` : ""}, ${selectedOrder.deliveryComuna || ""}, ${selectedOrder.deliveryCity || ""}`;

    // Build HTML string first (for blob URL approach - works on mobile)
    const htmlContent = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Orden de Pago - ${orderNumber}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            color: #000;
            font-size: 12px;
            line-height: 1.5;
            max-width: 800px;
            margin: 0 auto;
          }
          .border-box {
            border: 1px solid #000;
            padding: 10px;
            margin-bottom: 15px;
          }
          .header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
          }
          .logo {
            width: 150px;
            height: auto;
          }
          .company-info {
            text-align: center;
            font-weight: bold;
            width: 100%;
            margin: 0 20px;
          }
          .rut-box {
            border: 2px solid red;
            padding: 10px;
            color: red;
            font-weight: bold;
            text-align: center;
            line-height: 1.3;
          }
          .notification-box {
            border: 1px solid #000;
            padding: 10px;
            margin: 15px 0;
            text-align: center;
            background-color: #f9f9f0;
            font-weight: bold;
            color: #6b5900;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border-bottom: 1px solid #000;
            padding: 8px;
            text-align: left;
          }
          th {
            font-weight: bold;
          }
          .total-row td {
            font-weight: bold;
          }
          .info-grid {
            display: grid;
            grid-template-columns: auto auto;
            margin-bottom: 15px;
          }
          .info-label {
            font-weight: bold;
            margin-right: 5px;
          }
          .payment-details {
            margin-top: 20px;
            font-weight: bold;
          }
          .address-block {
            text-align: center;
            margin-bottom: 15px;
          }
          .references {
            text-align: center;
            margin: 20px 0;
            border-top: 1px solid #000;
            border-bottom: 1px solid #000;
            padding: 10px 0;
          }
          .footer {
            text-align: center;
            font-size: 10px;
            margin-top: 30px;
          }
          .product-image {
            width: 50px;
            height: auto;
            margin-right: 10px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <img src="/aliace-logo.jpg" alt="Aliace Logo" class="logo" />
          </div>
          <div class="company-info">
            <div style="font-size: 16px;">ALIMENTOS Y ACEITES SPA</div>
            <div>COMERCIALIZACION Y DISTRIBUCION DE PRODUCTOS ALIMENTICIOS</div>
            <div>CAUPOLICAN 9261 BODEGA D</div>
            <div>QUILICURA - SANTIAGO</div>
            <div>TELEFONOS: (56-2) 3340 0800</div>
          </div>
          <div style="width: 250px;">
            <div class="date" style="text-align: right; margin-bottom: 5px;">${formattedDate}, 5:35 AM</div>
            <div class="rut-box">
              R.U.T. 76.505.808-2<br/>
              ORDEN DE PAGO<br/>
              Nº ${orderNumber}
            </div>
          </div>
        </div>

        <div class="notification-box">
          Este documento es una orden de pago. Una vez pagado, se gestionará el despacho de tu envío automáticamente
        </div>

        <div class="info-grid" style="display: flex; flex-wrap: wrap;">
          <div style="width: 50%; margin-bottom: 10px;">
            <div><span class="info-label">SEÑOR(ES):</span> ${clientName}</div>
            <div><span class="info-label">DIRECCIÓN:</span> ${clientAddress}</div>
            <div><span class="info-label">COMUNA:</span> ${clientComuna}</div>
            ${activityHtml}
            <div><span class="info-label">COD. DE VENTA:</span> TRANSFERENC. ANTICIP</div>
          </div>
          <div style="width: 50%; margin-bottom: 10px;">
            <div><span class="info-label">FECHA:</span> ${formattedDate}</div>
            <div><span class="info-label">R.U.T.:</span> ${clientTaxId}</div>
            <div><span class="info-label">TELÉFONO:</span> ${clientPhone}</div>
            <div><span class="info-label">VENCIMIENTO:</span> ${formattedDate}</div>
            <div><span class="info-label">VENDEDOR:</span> ${seller.lastname}</div>
          </div>
        </div>

        <div class="address-block" style="border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 10px 0;">
          <div><span class="info-label">DIRECCIÓN DE DESPACHO</span></div>
          <div>${deliveryAddressText}</div>
        </div>

        <div class="references" style="border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 10px 0;">
          <div>REFERENCIAS</div>
          <div>NV ${orderNumber}</div>
        </div>

        <table style="width: 100%; margin-top: 20px;">
          <thead>
            <tr>
              <th>PRODUCTO</th>
              <th>CANTIDAD</th>
              <th>PRECIO UNIT.</th>
              <th>DCTO</th>
              <th>TOTAL</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHtml}
          </tbody>
        </table>

        <div class="payment-details" style="margin-top: 25px;">
          <div style="display: flex; justify-content: space-between;">
            <div style="width: 60%;">
              <div>TRANSFERENCIAS Y DEPOSITOS</div>
              <div>A NOMBRE DE: ALIMENTOS Y ACEITES SPA</div>
              <div>BANCO SANTANDER - NºCTA 69977499</div>
              <div>BANCO CHILE - NºCTA 00-800-57631-04</div>
              <div>CORREO: pagos@aliace.cl</div>
              <div>POR FAVOR INDICAR Nº ORDEN ${orderNumber} AL PAGAR</div>
            </div>
            <div style="width: 40%; text-align: right;">
              <div>NETO: $${subtotal.toLocaleString("es-CL")}</div>
              <div>EXENTO: -</div>
              <div>IVA (19%): $${iva.toLocaleString("es-CL")}</div>
              <div>TOTAL: $${total.toLocaleString("es-CL")}</div>
            </div>
          </div>
        </div>

        <div class="footer" style="margin-top: 50px; text-align: right;">
          <div>about:blank</div>
          <div>1/1</div>
        </div>

        <script>
          window.onload = function() {
            // Wait a moment for images to load before printing
            setTimeout(() => {
              window.print();
            }, 500);
          }
        </script>
      </body>
      </html>
    `;

    // Create blob URL from HTML content (works on mobile browsers)
    const blob = new Blob([htmlContent], { type: "text/html" });
    const blobUrl = URL.createObjectURL(blob);

    // Open blob URL in new window
    const printWindow = window.open(blobUrl, "_blank");

    if (!printWindow) {
      // Fallback: trigger download if popup still blocked
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `orden-de-pago-${orderNumber}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      return "download";
    }

    // Cleanup blob URL after page loads
    printWindow.addEventListener("load", () => {
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    });

    return true;
  } catch (error) {
    console.error("Error generating payment document:", error);
    return false;
  }
}
